import { getProductColorHex, colorContributionRampBucketKey } from "./ColorContributionRamp";
import { formatSizeLabel, getProductWhRatioKey } from "./sizeUtils";

/** Set to `true` to show COLOUR · SOH % ramps (header row + GP slide card) and compute SOH-by-colour data. */
export const SOH_COLOR_RAMP_ENABLED = false;

/** Period sell-through: sold in date range ÷ buy (inward) qty. */
export const getProductPeriodStPct = (product) => {
	const buy = Number(product?.troco_ls?.t_buy) || 0;
	const sold = Number(product?.troco_ls?.t_sale) || 0;
	if (buy <= 0) return 0;
	return Math.round((sold / buy) * 10000) / 100;
};

export const parseProductCogs = (product) => parseFloat(String(product?.COGS ?? "").replace(/,/g, "")) || 0;

/** Per-option GP (₹) — round each option, then sum in breakdowns (consistent with contribution chips). */
export const getProductGpValue = (product) => Math.round(parseFloat(product?.lcogs?.gp_value || 0));

export const sumGpValueForProducts = (products, { onlyPositive = false } = {}) =>
	(products || []).reduce((sum, product) => {
		const gp = getProductGpValue(product);
		if (onlyPositive && gp <= 0) return sum;
		return sum + gp;
	}, 0);

/** COGS buy value per option (COGS × T.INW) for colour-ramp contribution on the T.INW bar. */
export const getProductBuyCogsValue = (product) => {
	const cogs = parseProductCogs(product);
	const inw = Number(product?.troco_ls?.t_inw) || 0;
	return Math.round(cogs * inw);
};

/**
 * Period realisation off MRP for one option. ASP is realised value ÷ units sold in the range,
 * the same basis as the summary header's Avg. ASP, and the discount figures reuse it so
 * MRP × (1 − Disc%) always resolves back to ASP. Nulls mean "nothing sold in the period".
 * Note this differs from the backend `discount_percentage`, which is based on net (taxable) amount.
 */
export const getProductRealisation = (product) => {
	const realised = parseFloat(String(product?.realised_value ?? "").replace(/,/g, "")) || 0;
	const unitsSold = Number(product?.troco_ls?.t_sale) || 0;
	const mrp = parseFloat(String(product?.troco_ls?.mrp ?? "").replace(/,/g, "")) || 0;
	if (unitsSold <= 0 || realised <= 0) return { asp: null, discountPct: null, discountValue: null };
	const asp = realised / unitsSold;
	if (mrp <= 0) return { asp, discountPct: null, discountValue: null };
	return {
		asp,
		discountPct: ((mrp - asp) / mrp) * 100,
		discountValue: mrp * unitsSold - realised,
	};
};

export const formatRos = (value) => {
	const n = Number(value);
	if (value == null || value === "" || Number.isNaN(n) || n <= 0) return "—";
	return n.toFixed(2);
};

// Format number without decimals — Indian numbering system (e.g. 10,00,000)
export const formatNumber = (num) => {
	return Math.round(num).toLocaleString("en-IN");
};

/** Per-piece averages; MRP uses 3 dp so Avg × Pcs is closer to Value than integer rounding. */
export const formatAvgRate = (num, fractionDigits = 2) => {
	if (num == null || !Number.isFinite(Number(num))) return "0";
	return Number(num).toLocaleString("en-IN", {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	});
};

// Format number with 2 decimal places for GP
export const formatGPPercentage = (num) => {
	const value = Number(num);
	// If value is very small but not zero, show as 0.01 to avoid showing 0
	if (value > 0 && value < 0.01) {
		return 0.01;
	}
	return Math.round(value * 100) / 100;
};

// Format number with 2 decimal places for GP value
export const formatGPValue = (num) => {
	const value = Number(num);
	// If value is very small but not zero, round to 1 to avoid showing 0
	if (value > 0 && value < 1) {
		return 1;
	}
	return Math.round(value).toLocaleString("en-IN");
};

/** Ramp buckets by **colour label**, not resolved hex (avoids Off White + White merging to one #fff key). */
export function colorContributionMapKey(product) {
	return colorContributionRampBucketKey(product);
}

/**
 * Per-colour % of total for a product metric (only rows with positive values contribute).
 * Row `soldQty` holds the summed units; `hex` is set when the product exposes a hex (or `color` is hex).
 * Optional `getGpValue` — when passed, sum a secondary value per colour and attach `gpSharePct` (% of that total in the slice; sold ramp = GP, T.INW ramp = COGS buy value); bar width stays from the primary metric.
 */
export function aggregateColorMetricContributions(products, brandFilter, getMetricValue, getGpValue) {
	if (!products || products.length === 0) return [];

	let list = products;
	if (brandFilter) {
		list = products.filter((p) => (p.brand || "Unknown").toLowerCase() === String(brandFilter).toLowerCase());
	}

	const colorMap = new Map();
	list.forEach((product) => {
		const colorName = String(product.color ?? "Unknown").trim() || "Unknown";
		const hex = getProductColorHex(product);
		const mapKey = colorContributionMapKey(product);
		const qty = Math.round(Number(getMetricValue(product)) || 0);
		const gpAdd = typeof getGpValue === "function" ? getProductGpValue(product) : 0;
		if (qty > 0) {
			if (colorMap.has(mapKey)) {
				const existing = colorMap.get(mapKey);
				existing.soldQty += qty;
				existing.count += 1;
				if (typeof getGpValue === "function") {
					existing.gpValue = (existing.gpValue || 0) + gpAdd;
				}
				if (!existing.hex && hex) existing.hex = hex;
			} else {
				colorMap.set(mapKey, {
					color: colorName,
					...(hex ? { hex } : {}),
					soldQty: qty,
					...(typeof getGpValue === "function" ? { gpValue: gpAdd } : {}),
					count: 1,
				});
			}
		}
	});

	const buckets = Array.from(colorMap.values());
	const totalSold = buckets.reduce((sum, item) => sum + item.soldQty, 0);
	const totalGp =
		typeof getGpValue === "function" ? buckets.reduce((sum, item) => sum + (item.gpValue || 0), 0) : 0;

	return buckets
		.map((item) => ({
			...item,
			percentage: totalSold > 0 ? Math.round((item.soldQty / totalSold) * 100 * 10) / 10 : 0,
			...(typeof getGpValue === "function" && totalGp > 0
				? {
						gpSharePct: Math.round(((item.gpValue || 0) / totalGp) * 100 * 10) / 10,
					}
				: {}),
		}))
		.filter((item) => item.soldQty > 0)
		.sort((a, b) => b.soldQty - a.soldQty);
}

/**
 * Per-colour SOH with WH vs EBO split (soldQty = WH + EBO for ramp width / %).
 */
export function aggregateColorSohContributions(products, brandFilter) {
	if (!products || products.length === 0) return [];

	let list = products;
	if (brandFilter) {
		list = products.filter((p) => (p.brand || "Unknown").toLowerCase() === String(brandFilter).toLowerCase());
	}

	const colorMap = new Map();
	list.forEach((product) => {
		const colorName = String(product.color ?? "Unknown").trim() || "Unknown";
		const hex = getProductColorHex(product);
		const mapKey = colorContributionMapKey(product);
		const wh = Math.round(Number(product.total_wh_soh) || 0);
		const ebo = Math.round(Number(product.total_ebo_soh) || 0);
		const total = wh + ebo;
		if (total > 0) {
			if (colorMap.has(mapKey)) {
				const existing = colorMap.get(mapKey);
				existing.soldQty += total;
				existing.whSoh += wh;
				existing.eboSoh += ebo;
				existing.count += 1;
				if (!existing.hex && hex) existing.hex = hex;
			} else {
				colorMap.set(mapKey, {
					color: colorName,
					...(hex ? { hex } : {}),
					soldQty: total,
					whSoh: wh,
					eboSoh: ebo,
					count: 1,
				});
			}
		}
	});

	const grandTotal = Array.from(colorMap.values()).reduce((sum, item) => sum + item.soldQty, 0);

	return Array.from(colorMap.values())
		.map((item) => ({
			...item,
			percentage: grandTotal > 0 ? Math.round((item.soldQty / grandTotal) * 100 * 10) / 10 : 0,
		}))
		.filter((item) => item.soldQty > 0)
		.sort((a, b) => b.soldQty - a.soldQty);
}

/** Per-fashionability % of sold qty (period or overall) for straight divider bar. */
export function aggregateFashionabilityMetricContributions(products, brandFilter, getQty) {
	if (!products || products.length === 0) return [];

	let list = products;
	if (brandFilter) {
		list = products.filter((p) => (p.brand || "Unknown").toLowerCase() === String(brandFilter).toLowerCase());
	}

	const fashionMap = new Map();
	list.forEach((product) => {
		const label = String(product.fashionability ?? "Unknown").trim() || "Unknown";
		const mapKey = label.replace(/\s+/g, " ").toLowerCase();
		const qty = Math.round(Number(getQty(product)) || 0);
		if (qty > 0) {
			if (fashionMap.has(mapKey)) {
				fashionMap.get(mapKey).soldQty += qty;
			} else {
				fashionMap.set(mapKey, { fashionability: label, soldQty: qty });
			}
		}
	});

	const buckets = Array.from(fashionMap.values());
	const totalSold = buckets.reduce((sum, item) => sum + item.soldQty, 0);

	return buckets
		.map((item) => ({
			...item,
			percentage: totalSold > 0 ? Math.round((item.soldQty / totalSold) * 100 * 10) / 10 : 0,
		}))
		.filter((item) => item.soldQty > 0)
		.sort((a, b) => b.soldQty - a.soldQty);
}

export const aggregateFashionabilitySoldContributions = (products, brandFilter) =>
	aggregateFashionabilityMetricContributions(products, brandFilter, (p) => p.troco_ls?.t_sale);

export const aggregateFashionabilityOverallContributions = (products, brandFilter) =>
	aggregateFashionabilityMetricContributions(products, brandFilter, (p) => p.troco_ls?.t_sale_total);

// Helper function for client-side sorting
export const sortProducts = (products, sortField, sortDirection) => {
	if (products.length === 0) {
		return products;
	}

	// Map field names to actual data properties
	const getSortValue = (p) => {
		switch (sortField) {
			case "period_st":
				return getProductPeriodStPct(p);
			case "st":
				return parseFloat(p.troco_ls?.st) || 0;
			case "qty":
			case "sold_in_period":
				return parseFloat(p.troco_ls?.t_sale) || 0;
			case "total_sold":
				return parseFloat(p.troco_ls?.t_sale_total) || 0;
			case "inw_qty":
				// Inward / T.INW quantity
				return parseFloat(String(p.troco_ls?.t_inw ?? "").replace(/,/g, "")) || 0;
			case "cogs":
				return parseProductCogs(p);
			case "gp_cont":
				// GP Contribution: gp_value
				return parseFloat(p.lcogs?.gp_value) || 0;
			case "period_ros":
				return parseFloat(p.period_ros) || 0;
			case "ros":
				return parseFloat(p.rate_of_sale) || 0;
			case "doh":
				// Days on Hand from the backend; null = stock but no sales (unbounded) sorts as most overstocked
				return p.avg_doh == null ? Number.MAX_SAFE_INTEGER : Number(p.avg_doh) || 0;
			case "aging": {
				if (p.avg_aging != null && p.avg_aging !== "" && !Number.isNaN(Number(p.avg_aging))) {
					return Number(p.avg_aging);
				}
				const vals = [p.ebo_aging || 0, p.lfs_aging || 0, p.mbo_aging || 0, p.ecom_aging || 0].filter((v) => v > 0);
				return vals.length > 0 ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0;
			}
			case "max_recv_aging":
			case "last_aging": {
				if (p.avg_max_aging != null && p.avg_max_aging !== "" && !Number.isNaN(Number(p.avg_max_aging))) {
					return Number(p.avg_max_aging);
				}
				const vals = [p.ebo_max_aging || 0, p.lfs_max_aging || 0, p.mbo_max_aging || 0, p.ecom_max_aging || 0].filter(
					(v) => v > 0
				);
				return vals.length > 0 ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0;
			}
			case "wh_soh":
				return Number(p.total_wh_soh) || 0;
			case "ebo_soh":
				return Number(p.total_ebo_soh) || 0;
			case "multi_mrp_cogs": {
				const mrp = parseFloat(String(p.troco_ls?.mrp ?? "").replace(/,/g, "")) || 0;
				const cogs = parseFloat(String(p.COGS ?? "").replace(/,/g, "")) || 0;
				if (!cogs) return 0;
				return mrp / cogs;
			}
			default:
				// Unknown field — key 0 for every row keeps the original order (stable sort)
				return 0;
		}
	};

	// Decorate-sort-undecorate: compute each row's sort key once instead of inside every comparison
	const decorated = products.map((product) => [getSortValue(product), product]);
	decorated.sort((a, b) => (sortDirection === "asc" ? a[0] - b[0] : b[0] - a[0]));
	return decorated.map((entry) => entry[1]);
};

/** CSV column aliases for the palette-type segment of a product key. */
const PRODUCT_KEY_PALETTE_COLUMNS = ["pallete type", "palette type", "pallete_type", "palette_type", "palette"];

const getCsvRowValue = (row, ...columnNames) => {
	for (const name of columnNames) {
		const val = row?.[name];
		if (val != null && String(val).trim()) return String(val).trim();
	}
	return "";
};

/** Build BRAND-DEPARTMENT-STYLE-COLOR-PALLETE_TYPE from one parsed CSV row. */
export const buildProductKeyFromCsvRow = (row) =>
	[
		getCsvRowValue(row, "brand"),
		getCsvRowValue(row, "department"),
		getCsvRowValue(row, "style"),
		getCsvRowValue(row, "color"),
		getCsvRowValue(row, ...PRODUCT_KEY_PALETTE_COLUMNS),
	].join("-");

/** Unique product keys from uploaded CSV rows (skips blank rows). */
export const buildProductKeysFromCsvRows = (rows) => {
	const seen = new Set();
	const keys = [];
	for (const row of rows || []) {
		const key = buildProductKeyFromCsvRow(row);
		if (!key.split("-").some((part) => part.length > 0)) continue;
		if (!seen.has(key)) {
			seen.add(key);
			keys.push(key);
		}
	}
	return keys;
};

// Mapping from filter field name to the selectedItems / filterOptions key
export const filterKeyMap = {
	key: "keys",
	brand: "brands",
	subBrand: "subBrands",
	department: "departments",
	season: "seasons",
	city: "cities",
	subCategory: "subCategories",
	palette: "palettes",
	fashionability: "fashionability",
	fitType: "fitTypes",
	fabricBlend: "fabricBlends",
	size: "sizes",
	nod: "nods",
	channel: "channels",
	store: "stores",
	state: "states",
	region: "regions",
	tier: "tiers",
	aging: "aging",
	st_percentage: "st_percentage",
	// add more if needed
};

/** Selected values first (stable order within each group) so open dropdowns show current choices at the top. */
export const orderOptionsSelectedFirst = (optionsList, selected) => {
	if (!optionsList?.length || !selected || selected.size === 0) return optionsList;
	const atTop = [];
	const rest = [];
	for (const o of optionsList) {
		if (selected.has(o)) atTop.push(o);
		else rest.push(o);
	}
	return [...atTop, ...rest];
};

/** Sale-channel keys for period vs overall sold-pcs split in the summary header. */
export const PICTORIAL_CHANNEL_SOLD_DEF = [
	{ label: "EBO", periodKey: "ebo_sold_qty", overallKey: "ebo_sold_qty_total" },
	{ label: "LFS", periodKey: "lfs_sold_qty", overallKey: "lfs_sold_qty_total" },
	{ label: "MBO", periodKey: "mbo_sold_qty", overallKey: "mbo_sold_qty_total" },
	{ label: "D2C", periodKey: "d2c_sold_qty", overallKey: "d2c_sold_qty_total" },
	{ label: "MP", periodKey: "marketplace_sold_qty", overallKey: "marketplace_sold_qty_total" },
	{ label: "Other", periodKey: "other_sold_qty", overallKey: "other_sold_qty_total" },
];

export const channelSoldContributionPct = (part, total) => {
	if (!total || total <= 0) return "0.0";
	return (Math.round((part / total) * 1000) / 10).toFixed(1);
};

/**
 * Portfolio summary for the top cards (INW / Sold / Performance).
 * Pass the same product list the grid is showing (colour ramp + quick filters).
 */
export function aggregatePictorialSummaryTotals(products, { startDate, endDate, companyTotals, subCategory } = {}) {
	const list = products || [];
	if (list.length === 0) {
		return {
			totalBuy: 0,
			totalSold: 0,
			totalSoldLifetime: 0,
			subCategory: subCategory || "All",
			avgASP: 0,
			avgMRP: 0,
			avgMRPSold: 0,
			buyAvgCOGS: 0,
			avgCOGS: 0,
			gpValue: 0,
			gpPercentage: 0,
			mrpValue: 0,
			soldMRPValue: 0,
			buyCogsValue: 0,
			soldCogsValue: 0,
			buyMulti: 0,
			soldMulti: 0,
			percentageSold: 0,
			salesContribution: 0,
			gpContribution: 0,
			currentSoh: 0,
			stockCoverOneMonth: 0,
			netSalesValue: 0,
			discountPctAvg: 0,
		};
	}

	// One fused pass over the list (was 14 separate reduce/map passes); each product's
	// COGS / MRP strings are parsed once and reused across the accumulators.
	let totalBuy = 0;
	let totalSold = 0;
	let totalSoldLifetime = 0;
	let currentSoh = 0;
	let totalCOGS = 0;
	let totalGPValue = 0;
	let totalGPPercentage = 0;
	let totalRealisedValue = 0;
	let netSalesValue = 0;
	let mrpValueExact = 0;
	let soldMRPValueExact = 0;
	let buyCogsValue = 0;
	let soldCogsValue = 0;
	let discountSum = 0;
	let discountCount = 0;

	for (const product of list) {
		const tBuy = product.troco_ls?.t_buy || 0;
		const tSale = product.troco_ls?.t_sale || 0;
		totalBuy += tBuy;
		totalSold += tSale;
		totalSoldLifetime += product.troco_ls?.t_sale_total || 0;
		currentSoh = currentSoh + (Number(product.total_wh_soh) || 0) + (Number(product.total_ebo_soh) || 0);
		totalCOGS += parseFloat(product.COGS || 0);
		totalGPValue += getProductGpValue(product);
		totalGPPercentage += parseFloat(product.lcogs?.gp_percentage?.replace("%", "") || 0);
		totalRealisedValue += parseFloat(product.realised_value || 0);
		netSalesValue += parseFloat(product.net_amount || 0);
		const mrp = parseFloat(product.troco_ls?.mrp || 0);
		mrpValueExact += mrp * tBuy;
		soldMRPValueExact += mrp * tSale;
		const cleanCogs = parseFloat(String(product.COGS ?? "").replace(/,/g, "")) || 0;
		buyCogsValue += cleanCogs * tBuy;
		soldCogsValue += cleanCogs * tSale;
		const discount = parseFloat(product.discount_percentage || 0);
		if (!Number.isNaN(discount) && discount >= 0) {
			discountSum += discount;
			discountCount += 1;
		}
	}

	const start = startDate ? new Date(startDate) : null;
	const end = endDate ? new Date(endDate) : null;
	const periodDays =
		start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())
			? Math.max(1, Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1)
			: 1;
	const monthlySoldRate = totalSold > 0 ? (totalSold / periodDays) * 30 : 0;
	const stockCoverOneMonth = monthlySoldRate > 0 ? currentSoh / monthlySoldRate : 0;

	const avgCOGS = totalCOGS / list.length;
	const avgGPPercentage = Math.round((totalGPPercentage / list.length) * 100) / 100;

	const avgASP = totalSold > 0 && totalRealisedValue > 0 ? totalRealisedValue / totalSold : 0;

	const avgMRPBuy = totalBuy > 0 ? Math.round(mrpValueExact / totalBuy) : 0;
	const mrpValue = avgMRPBuy * totalBuy;
	const avgMRPSold = totalSold > 0 ? Math.round(soldMRPValueExact / totalSold) : 0;
	const soldMRPValue = avgMRPSold * totalSold;
	const buyAvgCOGS = totalBuy > 0 ? buyCogsValue / totalBuy : 0;
	const buyMulti = buyCogsValue > 0 ? mrpValue / buyCogsValue : 0;
	const soldMulti = soldCogsValue > 0 ? soldMRPValue / soldCogsValue : 0;
	const percentageSold = totalBuy > 0 ? (totalSoldLifetime / totalBuy) * 100 : 0;

	const companySold = companyTotals?.totalSold || 0;
	const companyGp = companyTotals?.gpValue || 0;
	const salesContribution = companySold > 0 ? (totalSold / companySold) * 100 : 100;
	const rawGpContribution = companyGp > 0 ? (totalGPValue / companyGp) * 100 : 0;
	const gpContribution = Math.min(Math.round(rawGpContribution * 10) / 10, 100);

	const discountPctAvg = discountCount > 0 ? discountSum / discountCount : 0;

	return {
		totalBuy,
		totalSold,
		totalSoldLifetime,
		subCategory: subCategory || "All",
		avgASP,
		avgMRP: avgMRPBuy,
		avgMRPSold,
		buyAvgCOGS,
		avgCOGS,
		gpValue: totalGPValue,
		gpPercentage: avgGPPercentage,
		mrpValue,
		soldMRPValue,
		buyCogsValue,
		soldCogsValue,
		buyMulti,
		soldMulti,
		percentageSold,
		salesContribution,
		gpContribution,
		currentSoh,
		stockCoverOneMonth,
		netSalesValue,
		discountPctAvg,
	};
}

const DEFAULT_TROCO = { t_buy: 0, t_inw: 0, t_sale: 0, st: 0, mrp: "0" };
const DEFAULT_LCOGS = {
	gp_percentage: "0.00%",
	gp_value: "0.00",
	oln_cont: "0%",
	offln_cont: "0%",
	sales_cont: "0%",
};

/** Ensure nested metric objects exist without cloning every field. */
export const normalizePictorialProduct = (product) => ({
	...product,
	troco_ls: product?.troco_ls || DEFAULT_TROCO,
	lcogs: product?.lcogs || DEFAULT_LCOGS,
});

const normAttr = (v) => String(v || "").toUpperCase().trim();

/** Season fallbacks for broken-pivotal recommendations (AW-26 → SS-26 → AW-25 …). */
export function getSeasonFallbacks(season, max = 8) {
	const s = String(season || "").trim().toUpperCase();
	const m = s.match(/^(AW|SS)[- ]?(\d{2,4})$/);
	if (!m) return [];
	const type = m[1];
	const rawYear = m[2];
	const year = parseInt(rawYear.length === 4 ? rawYear.slice(-2) : rawYear, 10);
	const sep = s.includes("-") ? "-" : s.includes(" ") ? " " : "";
	const pad = (y) => String(y).padStart(rawYear.length, "0");
	const fallbacks = [];
	let t = type;
	let y = year;
	for (let i = 0; i < max; i++) {
		if (t === "AW") {
			t = "SS";
		} else {
			t = "AW";
			y -= 1;
		}
		fallbacks.push(`${t}${sep}${pad(y)}`);
	}
	return fallbacks;
}

const isProductNonBrokenPivotal = (p) => {
	const altPivRows = Array.isArray(p.size_soh_breakdown)
		? p.size_soh_breakdown.filter((r) => r.pivotal || r.is_pivotal)
		: [];
	if (altPivRows.length === 0) {
		return (p.pivotal_wh_soh || 0) > 0 && (p.pivotal_ebo_soh || 0) > 0;
	}
	return altPivRows.every((r) => (r.wh_soh || 0) > 0 && (r.ebo_soh || 0) > 0);
};

/** Index key = normalized attribute tuple; NUL separator cannot appear in attribute text. */
const brokenRecsIndexKey = (brand, category, subCategory, color, palette, season) =>
	[brand, category, subCategory, color, palette, season].join("\u0000");

/**
 * One pass over the list: bucket non-broken-pivotal products by
 * brand/cat/subcat/color/palette/season so each broken product resolves its
 * recommendations with Map lookups instead of full-list scans per season.
 * Buckets keep list order, so filter → sort → slice matches the old scan exactly.
 */
export function buildBrokenRecommendationsIndex(allProducts) {
	const index = new Map();
	for (const p of allProducts || []) {
		if (!isProductNonBrokenPivotal(p)) continue;
		const key = brokenRecsIndexKey(
			normAttr(p.brand),
			normAttr(p.category),
			normAttr(p.sub_category),
			normAttr(p.color),
			normAttr(p.palette),
			normAttr(p.season)
		);
		const bucket = index.get(key);
		if (bucket) bucket.push(p);
		else index.set(key, [p]);
	}
	return index;
}

/** Similar in-stock options for one broken product (same brand/cat/subcat/color/palette). */
export function findBrokenRecommendations(product, allProducts, limit = 4, prebuiltIndex = null) {
	if (!product || !allProducts?.length) return { items: [], fromSeason: null };

	const index = prebuiltIndex || buildBrokenRecommendationsIndex(allProducts);

	const myBrand = normAttr(product.brand);
	const myCat = normAttr(product.category);
	const mySubCat = normAttr(product.sub_category);
	const myColor = normAttr(product.color);
	const myPalette = normAttr(product.palette);

	const matchesForSeason = (targetSeason) => {
		const bucket = index.get(
			brokenRecsIndexKey(myBrand, myCat, mySubCat, myColor, myPalette, normAttr(targetSeason))
		);
		if (!bucket) return [];
		return bucket
			.filter((p) => p.PKEY !== product.PKEY)
			.sort(
				(a, b) =>
					(b.pivotal_wh_soh || 0) +
					(b.pivotal_ebo_soh || 0) -
					((a.pivotal_wh_soh || 0) + (a.pivotal_ebo_soh || 0))
			)
			.slice(0, limit);
	};

	const seasonList = [product.season, ...getSeasonFallbacks(product.season)];
	for (const season of seasonList) {
		if (!season) continue;
		const found = matchesForSeason(season);
		if (found.length > 0) return { items: found, fromSeason: normAttr(season) };
	}
	return { items: [], fromSeason: null };
};

/**
 * One pass over products: quick-filter counts, size labels, WH ratio keys,
 * and broken-pivotal recommendation map (avoids N×full-list scans in ProductCard).
 */
export function computePictorialInventoryAnalytics(products) {
	const list = products || [];
	const sizeCounts = new Map();
	const ratioKeys = new Set();
	const recommendationsByPkey = new Map();
	let whBroken = 0;
	let eboBroken = 0;
	let whAvailable = 0;
	let eboAvailable = 0;
	let whFullSizeSet = 0;
	let eboFullSizeSet = 0;

	const brokenProducts = [];

	for (const p of list) {
		const rows = Array.isArray(p.size_soh_breakdown) ? p.size_soh_breakdown : [];
		const pivRows = rows.filter((r) => r.pivotal || r.is_pivotal);

		const isWhBroken = pivRows.length > 0 && pivRows.some((r) => (r.wh_soh || 0) === 0);
		const isEboBroken = pivRows.length > 0 && pivRows.some((r) => (r.ebo_soh || 0) === 0);
		if (isWhBroken) whBroken += 1;
		if (isEboBroken) eboBroken += 1;

		const whAvailableOk =
			pivRows.length === 0 ? (p.pivotal_wh_soh || 0) > 0 : pivRows.every((r) => (r.wh_soh || 0) > 0);
		const eboAvailableOk =
			pivRows.length === 0 ? (p.pivotal_ebo_soh || 0) > 0 : pivRows.every((r) => (r.ebo_soh || 0) > 0);
		if (whAvailableOk) whAvailable += 1;
		if (eboAvailableOk) eboAvailable += 1;

		if (rows.length > 0 && rows.every((r) => (r.wh_soh || 0) > 0)) whFullSizeSet += 1;
		if (rows.length > 0 && rows.every((r) => (r.ebo_soh || 0) > 0)) eboFullSizeSet += 1;

		const labels = new Set();
		for (const row of rows) {
			const label = formatSizeLabel(row.size || row.size_label);
			if (label && label !== "?") labels.add(label);
		}
		for (const label of labels) {
			sizeCounts.set(label, (sizeCounts.get(label) || 0) + 1);
		}

		const ratioKey = getProductWhRatioKey(p);
		if (ratioKey) ratioKeys.add(ratioKey);

		if (isWhBroken || isEboBroken) brokenProducts.push(p);
	}

	if (brokenProducts.length > 0) {
		// One shared index → each broken product resolves via Map lookups, not full-list scans
		const recsIndex = buildBrokenRecommendationsIndex(list);
		for (const p of brokenProducts) {
			recommendationsByPkey.set(p.PKEY, findBrokenRecommendations(p, list, 4, recsIndex));
		}
	}

	return {
		whBrokenCount: whBroken,
		eboBrokenCount: eboBroken,
		whAvailableCount: whAvailable,
		eboAvailableCount: eboAvailable,
		whFullSizeSetCount: whFullSizeSet,
		eboFullSizeSetCount: eboFullSizeSet,
		sizeCounts,
		ratioKeys,
		recommendationsByPkey,
	};
}

/** Shopify CDN thumb for grid tiles; falls back to original URL. */
export function getProductImageThumbUrl(rawUrl, width = 400) {
	if (rawUrl == null) return "";
	const s = String(rawUrl).trim();
	if (!s || s === "-" || s === "N/A") return "";
	const lower = s.toLowerCase();
	if (lower === "null" || lower === "undefined") return "";
	try {
		const u = new URL(s);
		if (!u.hostname.includes("shopify") && !u.pathname.includes("/cdn/shop/")) {
			return s;
		}
		// Insert _WIDTHx before extension: image.jpg → image_400x.jpg
		const path = u.pathname;
		const replaced = path.replace(
			/\/([^/]+?)(\.(?:jpe?g|png|webp|gif))(_\d+x\d*|\.\w+)?$/i,
			(_, name, ext) => `/${name}_${width}x${ext}`
		);
		if (replaced !== path) {
			u.pathname = replaced;
			return u.toString();
		}
	} catch {
		/* keep original */
	}
	return s;
}
